import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import { StudentName, studentDisplayName } from "../utils/studentName";
import { leaveRangeShort } from "../utils/leaveRange";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

const API = process.env.REACT_APP_API;

const pad2 = (n) => String(n).padStart(2, '0');
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
// จำนวนวันลาแบบนับรวมวันเริ่มและวันสิ้นสุด (0 = ช่วงวันที่ไม่ถูกต้อง)
const dayCount = (start, end) => {
  if (!start || !end || start > end) return 0;
  const [y1, m1, d1] = start.split('-').map(Number);
  const [y2, m2, d2] = end.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000) + 1;
};
const DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;

export default function IslamicAdvisorLeave() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;
  const tl = t.advisorLeave;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);

  const [students, setStudents] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [homeLeaves, setHomeLeaves] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);   // study_id ของภาคสามัญ
  const [leaveStart, setLeaveStart] = useState(todayStr());
  const [leaveEnd, setLeaveEnd] = useState(todayStr());
  const [leaveType, setLeaveType] = useState('sick');
  const [leaveNote, setLeaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then(res => {
        const list = res.data || [];
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${API}/islamic-advisor/leaves`, { params: { yearId } })
      .then(res => {
        setStudents(res.data?.students || []);
        setLeaves(res.data?.leaves || []);
        setHomeLeaves(res.data?.homeLeaves || []);
      })
      .catch(() => { setStudents([]); setLeaves([]); setHomeLeaves([]); })
      .finally(() => { setLoaded(true); setLoading(false); });
  }, [yearId, setLoading]);

  useEffect(() => { load(); }, [load]);

  // แจ้งลาได้เฉพาะคนที่มีทะเบียนสามัญของปีนี้ เพราะ tb_student_leave คีย์ด้วย study_id
  const eligible = useMemo(() => students.filter(s => s.study_id != null), [students]);
  const missingRegular = students.length - eligible.length;

  // ช่วงลาที่นักเรียนแต่ละคนมีอยู่แล้ว — รวมใบกลับบ้านที่ฝ่ายปกครองอนุมัติด้วย
  // ใบกลับบ้านนับถึงก่อนวันที่กลับมาโรงเรียน (return_date คือวันที่มาเรียน ไม่ใช่วันลา)
  const existingByStudy = useMemo(() => {
    const map = new Map();
    const push = (studyId, entry) => {
      if (studyId == null) return;
      const key = String(studyId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    };
    leaves.forEach(l => push(l.study_id, { start: l.leave_start, end: l.leave_end, kind: 'leave', source: l.source }));
    const studyByStudent = new Map(students.map(s => [String(s.st_id), s.study_id]));
    homeLeaves.forEach(h => {
      const end = h.return_date ? addDays(h.return_date, -1) : h.leave_start;
      push(studyByStudent.get(String(h.st_id)), { start: h.leave_start, end, kind: 'home' });
    });
    return map;
  }, [leaves, homeLeaves, students]);

  const existingFor = useCallback((studyId) => {
    const list = existingByStudy.get(String(studyId)) || [];
    return list.filter(e => overlaps(leaveStart, leaveEnd, e.start, e.end));
  }, [existingByStudy, leaveStart, leaveEnd]);

  const overlapCount = selectedIds.filter(id => existingFor(id).length > 0).length;

  const toggleStudent = (studyId) => {
    setSelectedIds(prev => prev.includes(studyId)
      ? prev.filter(id => id !== studyId)
      : [...prev, studyId]);
  };
  // "เลือกทั้งหมด" = ทุกคนที่แจ้งลาได้และยังไม่มีใครแจ้งช่วงนี้ — เลือกคนที่ลาอยู่แล้ว
  // มาด้วยก็ถูก server ข้ามอยู่ดี แต่ครูจะนึกว่าบันทึกครบ
  const selectable = eligible.filter(s => existingFor(s.study_id).length === 0).map(s => s.study_id);
  const toggleAll = () => {
    setSelectedIds(prev => selectable.every(id => prev.includes(id)) && prev.length > 0 ? [] : selectable);
  };

  const handleStartChange = (val) => {
    const days = dayCount(leaveStart, leaveEnd);
    setLeaveStart(val);
    setLeaveEnd(days > 0 ? addDays(val, days - 1) : val);   // คงจำนวนวันลาเดิมไว้
  };
  const handleDaysChange = (val) => {
    if (!val) return;                                       // "กำหนดเอง" = ไม่แตะวันที่
    setLeaveEnd(addDays(leaveStart, Number(val) - 1));
  };

  const resetForm = () => {
    setSelectedIds([]);
    setLeaveStart(todayStr());
    setLeaveEnd(todayStr());
    setLeaveType('sick');
    setLeaveNote('');
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: tl.warnNoStudentTitle, text: tl.warnNoStudent });
      return;
    }
    if (leaveStart > leaveEnd) {
      Swal.fire({ icon: 'warning', title: tl.warnRangeTitle, text: tl.warnRange });
      return;
    }
    setSaving(true);
    axiosInstance.post(`${API}/islamic-advisor/leaves`, {
      yearId,
      study_ids: selectedIds,
      leave_start: leaveStart,
      leave_end: leaveEnd,
      leave_type: leaveType,
      leave_note: leaveNote.trim() || null,
    })
      .then((res) => {
        // server ข้ามคนที่มีการลาคาบเกี่ยวช่วงนี้อยู่แล้ว — ต้องบอกครูว่าข้ามใครไป
        const { inserted, skipped } = res.data || {};
        if (skipped > 0 && inserted === 0) {
          Swal.fire({ icon: 'info', title: tl.savedNoneTitle, text: tl.savedNoneText(skipped) });
        } else if (skipped > 0) {
          Swal.fire({ icon: 'success', title: tl.savedSomeTitle(inserted), text: tl.savedSomeText(skipped) });
        } else {
          Swal.fire({ icon: 'success', title: tl.savedTitle, timer: 1200, showConfirmButton: false });
        }
        resetForm();
        load();
      })
      .catch(err => {
        Swal.fire({ icon: 'error', title: tl.errorTitle, text: err.response?.data?.message || '' });
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (leave) => {
    Swal.fire({
      title: tl.deleteTitle,
      text: tl.deleteText(studentDisplayName(leave, lang)),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: tl.deleteConfirm,
      cancelButtonText: tl.cancel,
      confirmButtonColor: '#dc3545',
    }).then(result => {
      if (!result.isConfirmed) return;
      axiosInstance.delete(`${API}/islamic-advisor/leaves/${leave.leave_id}`, { params: { yearId } })
        .then(() => {
          Swal.fire({ icon: 'success', title: tl.deletedTitle, timer: 1000, showConfirmButton: false });
          load();
        })
        .catch(err => {
          Swal.fire({ icon: 'error', title: tl.errorTitle, text: err.response?.data?.message || '' });
        });
    });
  };

  const sourceLabel = (source) => ({
    discipline:      t.checkAttendance.leaveByDiscipline,
    academic:        t.checkAttendance.leaveByAcademic,
    islamic_advisor: t.checkAttendance.leaveByIslamicAdvisor,
  }[source] || t.checkAttendance.leaveByAdvisor);

  const typeLabel = (type) => type === 'personal' ? tl.typePersonal : tl.typeSick;

  const today = todayStr();
  const leaveDays = dayCount(leaveStart, leaveEnd);
  const roomOf = (studyId) => students.find(s => String(s.study_id) === String(studyId))?.regular_class || '-';

  // ใบกลับบ้านแสดงรวมในตารางเดียวกัน แต่แก้ที่นี่ไม่ได้ — ต้นทางอยู่ที่ฝ่ายปกครอง
  const rows = useMemo(() => {
    const byStudent = new Map(students.map(s => [String(s.st_id), s]));
    const homeRows = homeLeaves.map(h => ({
      key: `home-${h.leave_id}`,
      st_name: byStudent.get(String(h.st_id))?.st_name || '-',
      st_arabicname: byStudent.get(String(h.st_id))?.st_arabicname || null,
      study_id: byStudent.get(String(h.st_id))?.study_id ?? null,
      leave_start: h.leave_start,
      leave_end: h.return_date ? addDays(h.return_date, -1) : h.leave_start,
      leave_note: h.reason,
      home: true,
    }));
    const leaveRows = leaves.map(l => ({ ...l, key: `leave-${l.leave_id}`, home: false }));
    return [...leaveRows, ...homeRows].sort((a, b) => (
      a.leave_start === b.leave_start ? b.key.localeCompare(a.key) : (a.leave_start < b.leave_start ? 1 : -1)
    ));
  }, [leaves, homeLeaves, students]);

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <div className="fs-sm text-secondary mb-1">{tl.breadcrumb}</div>
          <h4 className="main-title mb-0">{tl.pageTitle}</h4>
        </div>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      <Alert variant="info" className="d-flex align-items-start gap-2 mb-4">
        <i className="ri-links-line fs-5"></i>
        <span>{tl.linkedNote}</span>
      </Alert>

      {loaded && students.length === 0 && (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">
            <i className="ri-information-line fs-3 d-block mb-2"></i>
            {tl.noRoom}
          </Card.Body>
        </Card>
      )}

      {students.length > 0 && (
        <Row className="g-3">
          {/* ── ฟอร์มแจ้งลา ── */}
          <Col xl="5">
            <Card className="card-one h-100">
              <Card.Header><Card.Title as="h6">{tl.formTitle}</Card.Title></Card.Header>
              <Card.Body className="p-3">
                <Row className="g-2 mb-3">
                  <Col xs="12">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">{tl.labelDays}</Form.Label>
                      <Form.Select
                        value={DAY_OPTIONS.includes(leaveDays) ? String(leaveDays) : ''}
                        onChange={e => handleDaysChange(e.target.value)}
                      >
                        <option value="">{tl.daysCustom}</option>
                        {DAY_OPTIONS.map(d => <option key={d} value={d}>{tl.daysUnit(d)}</option>)}
                      </Form.Select>
                      <Form.Text className="text-muted">{tl.daysHelp}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">{tl.labelStart}</Form.Label>
                      <Form.Control type="date" value={leaveStart} onChange={e => handleStartChange(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">{tl.labelEnd}</Form.Label>
                      <Form.Control type="date" value={leaveEnd} min={leaveStart} onChange={e => setLeaveEnd(e.target.value)} />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">{tl.labelType}</Form.Label>
                  <div className="d-flex gap-3">
                    {[['sick', tl.typeSick], ['personal', tl.typePersonal]].map(([value, label]) => (
                      <Form.Check
                        key={value}
                        type="radio"
                        name="islamicLeaveType"
                        id={`islamicLeaveType-${value}`}
                        label={label}
                        checked={leaveType === value}
                        onChange={() => setLeaveType(value)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">{tl.labelNote}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder={tl.notePlaceholder}
                    value={leaveNote}
                    onChange={e => setLeaveNote(e.target.value)}
                    maxLength={255}
                  />
                </Form.Group>

                <Form.Group>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="fw-semibold mb-0">{tl.labelStudents(selectedIds.length)}</Form.Label>
                    {eligible.length > 0 && (
                      <Button variant="link" size="sm" className="p-0" onClick={toggleAll}>
                        {selectedIds.length > 0 && selectedIds.length === selectable.length ? tl.clearAll : tl.selectAll}
                      </Button>
                    )}
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }} className="p-2">
                    {students.map(s => {
                      const already = s.study_id != null ? existingFor(s.study_id) : [];
                      return (
                        <div key={s.is_study_id} className="d-flex align-items-center gap-2 flex-wrap">
                          <Form.Check
                            type="checkbox"
                            id={`is-st-${s.is_study_id}`}
                            className="flex-grow-1"
                            disabled={s.study_id == null}
                            label={<>{s.st_idnumber} — <StudentName student={s} lang={lang} /></>}
                            checked={selectedIds.includes(s.study_id)}
                            onChange={() => toggleStudent(s.study_id)}
                          />
                          {s.study_id == null && <Badge bg="secondary">{tl.noRegularStudy}</Badge>}
                          {/* บอกด้วยว่าฝ่ายไหนแจ้งไว้ และช่วงวันไหน — "ลาอยู่แล้ว" เฉยๆ ไม่พอ
                              ให้ครูตัดสินใจว่าควรไปคุยกับใคร หรือที่จะแจ้งเป็นคนละช่วงกันไหม
                              วันที่ต้องอยู่บนหน้าจอ ไม่ใช่แค่ tooltip เพราะมือถือไม่มี hover */}
                          {already.map((e, i) => (
                            <Badge
                              key={i}
                              bg={e.kind === 'home' ? 'warning' : 'info'}
                              className="fw-normal"
                              title={e.start === e.end ? e.start : `${e.start} ${tl.rangeTo} ${e.end}`}
                            >
                              {e.kind === 'home' ? tl.homeLeave : sourceLabel(e.source)}{' '}
                              {leaveRangeShort(e.start, e.end)}
                            </Badge>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  {missingRegular > 0 && (
                    <Form.Text className="text-muted">{tl.noRegularHelp}</Form.Text>
                  )}
                </Form.Group>

                {overlapCount > 0 && (
                  <Alert variant="warning" className="mt-3 mb-0 py-2 px-3 fs-sm">
                    <i className="ri-alert-line me-1"></i>{tl.overlapWarn(overlapCount)}
                  </Alert>
                )}

                <div className="mt-3 d-flex justify-content-end">
                  <Button variant="success" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><Spinner animation="border" size="sm" className="me-1" />{tl.saving}</>
                      : <><i className="ri-save-line me-1"></i>{tl.save}</>}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* ── รายการลาที่มีอยู่ (ทุกฝ่ายที่แจ้ง ไม่ใช่เฉพาะของภาคศาสนา) ── */}
          <Col xl="7">
            <Card className="card-one h-100">
              <Card.Header><Card.Title as="h6">{tl.listTitle}</Card.Title></Card.Header>
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th scope="col">{tl.colName}</th>
                      <th scope="col" className="text-center" style={{ width: 90 }}>{tl.colRoom}</th>
                      <th scope="col" className="text-center" style={{ width: 170 }}>{tl.colRange}</th>
                      <th scope="col" className="text-center" style={{ width: 90 }}>{tl.colType}</th>
                      <th scope="col" style={{ width: 160 }}>{tl.colSource}</th>
                      <th scope="col">{tl.colNote}</th>
                      <th scope="col" className="text-center" style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? rows.map(row => (
                      <tr key={row.key} className={overlaps(today, today, row.leave_start, row.leave_end) ? 'table-info' : ''}>
                        <td><StudentName student={row} lang={lang} /></td>
                        <td className="text-center">{roomOf(row.study_id)}</td>
                        <td className="text-center">
                          {row.leave_start === row.leave_end
                            ? row.leave_start
                            : <>{row.leave_start}<br />{tl.rangeTo} {row.leave_end}</>}
                        </td>
                        <td className="text-center">
                          <Badge bg={row.home ? 'warning' : (row.leave_type === 'personal' ? 'info' : 'primary')}>
                            {row.home ? tl.homeLeave : typeLabel(row.leave_type)}
                          </Badge>
                        </td>
                        <td><small>{row.home ? t.checkAttendance.leaveByDiscipline : sourceLabel(row.source)}</small></td>
                        <td><small>{row.leave_note || '-'}</small></td>
                        <td className="text-center">
                          {!row.home && row.source === 'islamic_advisor' ? (
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          ) : (
                            <i className="ri-lock-line text-secondary" title={tl.onlyOwnDelete}></i>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={7} className="text-center text-muted py-3">{tl.noLeaves}</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
}
